import { TextAlignment } from '@/client/courses/types/courses.type';
import { t } from 'i18next';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { FlexContainer } from '../styled-ui';

const EDITABLE_PADDING_PX = 36;

const StyledText = styled.div<{
  multiline?: boolean;
  textAlignment?: TextAlignment;
  isBold?: boolean;
}>`
  max-width: 100%;
  min-width: 0;
  white-space: ${({ multiline }) => (multiline ? 'pre-wrap' : 'nowrap')};
  overflow: hidden;
  text-overflow: ${({ multiline }) => (multiline ? 'clip' : 'ellipsis')};
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: inherit;
  text-align: ${({ textAlignment }) => textAlignment || TextAlignment.LEFT};
  font-weight: ${({ isBold }) => (isBold ? 700 : 'inherit')};

  svg {
    margin-left: var(--xsmall-padding);
  }
`;

const StyledIcon = styled.i`
  cursor: pointer;
  padding: var(--xsmall-padding);
  color: var(--red-main);
  font-size: var(--heading-small-font-size);

  > svg {
    display: flex;
  }
`;

const StyledInput = styled.input<{
  textAlignment?: TextAlignment;
  isPurpleBackground?: boolean;
  isBold?: boolean;
}>`
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  font-weight: ${({ isBold }) => (isBold ? 700 : 'inherit')};
  padding: ${({ isBold }) => (isBold ? '0 8px 0 0' : '0') + ' !important'};
  margin: 0 !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: auto !important;
  min-height: auto !important;
  background: ${(props) =>
    props.isPurpleBackground
      ? 'var(--purple-dark)'
      : 'var(--beige-main)'} !important;
  margin-right: var(--xsmall-padding);
  color: ${(props) =>
    props.isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  caret-color: var(--red-main);
  vertical-align: baseline !important;
  text-align: ${(props) => props.textAlignment || TextAlignment.LEFT};
`;

const StyledTextarea = styled.textarea<{
  textAlignment?: TextAlignment;
  isPurpleBackground?: boolean;
  isBold?: boolean;
}>`
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  font-weight: ${({ isBold }) => (isBold ? 700 : 'inherit')};
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'} !important;
  margin-right: var(--xsmall-padding);
  color: ${(props) =>
    props.isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  caret-color: var(--red-main);
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  height: auto !important;
  min-height: auto !important;
  max-height: none;
  vertical-align: baseline !important;
  text-align: ${({ textAlignment }) => textAlignment || TextAlignment.LEFT};
`;

const StyledPlaceholder = styled.div<{
  textAlignment?: TextAlignment;
}>`
  color: var(--gray-darker);
  text-align: ${(props) => props.textAlignment || TextAlignment.LEFT};
  width: 100%;
`;

type EditableTextProps = {
  value: string;
  icon?: React.ReactNode;
  saveValue: (text: string) => void;
  multiline?: boolean;
  placeholder?: string;
  showTooltip?: boolean;
  textAlignment?: TextAlignment;
  isPurpleBackground?: boolean;
  isBold?: boolean;
};

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  icon,
  saveValue,
  multiline = false,
  placeholder,
  showTooltip = true,
  textAlignment = TextAlignment.LEFT,
  isPurpleBackground = false,
  isBold = false,
}) => {
  const id = `id-${v4()}`;
  const [isEditMode, setIsEditMode] = useState(false);
  const [text, setText] = useState(value);
  const [rows, setRows] = useState(1);
  const textRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const singleRowHeightRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isEditMode) {
      setText(value);
    }
  }, [value, isEditMode]);

  useEffect(() => {
    if (isEditMode && inputRef.current && !multiline) {
      inputRef.current.focus();
    }
  }, [isEditMode, multiline]);

  useEffect(() => {
    if (textareaRef.current && multiline && isEditMode) {
      if (!singleRowHeightRef.current) {
        const originalValue = textareaRef.current.value;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.rows = 1;
        textareaRef.current.value = 'M';
        void textareaRef.current.offsetHeight;
        singleRowHeightRef.current = textareaRef.current.scrollHeight;
        textareaRef.current.value = originalValue;
        void textareaRef.current.offsetHeight;
      }

      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const calculatedRows = Math.ceil(
        scrollHeight / singleRowHeightRef.current,
      );
      const finalRows = Math.max(calculatedRows, 1);

      setRows(finalRows);
      textareaRef.current.style.height = `${scrollHeight}px`;
    }
  }, [text, multiline, isEditMode]);

  useEffect(() => {
    if (isEditMode && multiline && textareaRef.current) {
      const len = textareaRef.current.value.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditMode, multiline]);

  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setText(e.target.value);
  };

  const handleEnterEditMode = () => {
    setRows(1);
    singleRowHeightRef.current = null;
    setIsEditMode(true);
  };

  const clearEditState = () => {
    setIsEditMode(false);
  };

  const handleSave = () => {
    saveValue(text);
    clearEditState();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setText(value);
      clearEditState();
    }
  };

  return !isEditMode ? (
    <FlexContainer
      justify={textAlignment === TextAlignment.CENTER ? 'center' : 'flex-start'}
      align="flex-start"
      className="relative"
      width="100%"
      style={{
        paddingRight: `${EDITABLE_PADDING_PX}px`,
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      <StyledText
        id={id}
        ref={textRef}
        onClick={handleEnterEditMode}
        multiline={multiline}
        textAlignment={textAlignment}
        isBold={isBold}
      >
        {text.length ? (
          <>
            {text}
            {icon && icon}
          </>
        ) : (
          <StyledPlaceholder textAlignment={textAlignment}>
            {placeholder}
          </StyledPlaceholder>
        )}
      </StyledText>

      {showTooltip && (
        <Tooltip
          position="right"
          target={`#${id}`}
          content={t('generic.clickToEdit')}
        />
      )}

      {!text.length && showTooltip && (
        <>
          <StyledIcon
            className="pi pi-exclamation-circle info"
            onClick={handleEnterEditMode}
          />
          <Tooltip
            position="right"
            target=".info"
            content={t('error.enterName')}
          />
        </>
      )}
    </FlexContainer>
  ) : (
    <FlexContainer
      justify={textAlignment === TextAlignment.CENTER ? 'center' : 'flex-start'}
      align="flex-start"
      className="relative"
      width="100%"
      style={{
        paddingRight: `${EDITABLE_PADDING_PX}px`,
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      {multiline ? (
        <StyledTextarea
          isBold={isBold}
          ref={textareaRef}
          autoFocus
          value={text}
          rows={rows}
          onChange={handleTextChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          textAlignment={textAlignment}
          isPurpleBackground={isPurpleBackground}
        />
      ) : (
        <StyledInput
          isBold={isBold}
          ref={inputRef}
          autoFocus
          value={text}
          onChange={handleTextChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          textAlignment={textAlignment}
          isPurpleBackground={isPurpleBackground}
        />
      )}
    </FlexContainer>
  );
};
